import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, List, Popover, Tag, Typography } from 'antd';
import { Bell } from 'react-feather';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../api';
import { isTriggerNotificationRecipient } from '../constants/triggerNotifications';
import { useAuthStore } from '../store';

dayjs.extend(relativeTime);

interface TriggerNotification {
  id: number;
  seller_id: string;
  seller_name: string;
  operation: string;
  status: 'WARNING' | 'FAILED';
  info?: {
    failed?: number;
    updated?: number;
    total?: number;
  };
  error_log?: string;
  created_at: string;
  is_read: boolean;
}

const POLL_INTERVAL_MS = 60_000;

const formatBadgeCount = (count: number) => (count > 99 ? '99+' : String(count));

const NotificationBell: React.FC = () => {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<TriggerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const canViewNotifications = isTriggerNotificationRecipient(user?.email);

  const fetchUnreadCount = useCallback(async () => {
    if (!canViewNotifications) return;

    try {
      const response = await api('notifications/unread-count', 'GET', null);
      if (response?.statusIsOk) {
        setUnreadCount(response.count || 0);
      }
    } catch {
      // silently ignore polling errors
    }
  }, [canViewNotifications]);

  const fetchNotifications = useCallback(async () => {
    if (!canViewNotifications) return;

    setLoading(true);
    try {
      const response = await api('notifications?limit=30', 'GET', null);
      if (response?.statusIsOk) {
        setNotifications(response.result || []);
      }
    } catch {
      // silently ignore fetch errors
    } finally {
      setLoading(false);
    }
  }, [canViewNotifications]);

  const markAsRead = async (notificationId: number) => {
    try {
      const response = await api(`notifications/${notificationId}/read`, 'POST', {});
      if (!response?.statusIsOk) return;

      setNotifications(prev =>
        prev.map(item => (item.id === notificationId ? { ...item, is_read: true } : item)),
      );
      await fetchUnreadCount();
    } catch {
      // silently ignore
    }
  };

  const markAllAsRead = async (event: React.MouseEvent) => {
    event.stopPropagation();

    setMarkingAllRead(true);
    try {
      const response = await api('notifications/read-all', 'POST', {});
      if (!response?.statusIsOk) return;

      setNotifications(prev => prev.map(item => ({ ...item, is_read: true })));
      await fetchUnreadCount();
    } catch {
      // silently ignore
    } finally {
      setMarkingAllRead(false);
    }
  };

  useEffect(() => {
    if (!canViewNotifications) return;

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [canViewNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (open && canViewNotifications) {
      fetchUnreadCount();
      fetchNotifications();
    }
  }, [open, canViewNotifications, fetchNotifications, fetchUnreadCount]);

  if (!canViewNotifications) {
    return null;
  }

  const content = (
    <div style={{ width: 380 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Typography.Text strong>Trigger alerts</Typography.Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" loading={markingAllRead} onClick={markAllAsRead}>
            Mark all read
          </Button>
        )}
      </div>
      {notifications.length === 0 && !loading ? (
        <Empty description="No trigger alerts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          loading={loading}
          size="small"
          dataSource={notifications}
          style={{ maxHeight: 360, overflowY: 'auto' }}
          renderItem={item => (
            <List.Item
              style={{
                cursor: 'pointer',
                background: item.is_read ? 'transparent' : '#f6ffed',
                padding: '8px 4px',
              }}
              onClick={() => !item.is_read && markAsRead(item.id)}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Tag color={item.status === 'FAILED' ? 'red' : 'orange'}>{item.status}</Tag>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      {item.seller_name}
                    </Typography.Text>
                  </div>
                }
                description={
                  <>
                    <Typography.Text style={{ fontSize: 12 }}>{item.operation}</Typography.Text>
                    {item.info && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {item.info.failed != null && `${item.info.failed} failed`}
                        {item.info.updated != null && ` · ${item.info.updated} updated`}
                        {item.info.total != null && ` · ${item.info.total} total`}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      {dayjs(item.created_at).fromNow()}
                    </div>
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <div className="header-badge" style={{ marginRight: 20 }}>
        <Bell color="#000000" size={20} strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="header-badge__count">{formatBadgeCount(unreadCount)}</span>
        )}
      </div>
    </Popover>
  );
};

export default NotificationBell;
